import type { ITradingActivityReportQuery } from '../../../generated/graphql.tsx';
import type { TupleKeyMap } from '../../TupleKeyMap.ts';
import { mapSubAccountAndDimensionToSubFund } from '../report/Report.utils.tsx';
import { dateReadableValueGetter } from 'components/technical/grids/agGrid.utils.tsx';

import {
  accountColumn,
  assetWithSymbolColumn,
  clusterColumn,
  subAccountColumn,
  subFundGroupColumn,
  underlyingAssetColumn,
  venueColumn,
} from '../../technical/grids/SharedReportColumns.tsx';
import { formatEnum } from '../../formatter.utils.ts';
import type { ReactElement } from 'react';
import type { ColDef, ColGroupDef, ValueGetterParams, GridApi } from 'ag-grid-community';
import { bignumber } from 'mathjs';
import GAgGridPresets from '../../technical/grids/GAgGridPresets.tsx';
import { defaultPresets } from './tradingActivityDefaultPreset.ts';
import { useMemo, useRef } from 'react';

type RowData = ITradingActivityReportQuery['bookkeeping']['tradingActivity'][number];

export const TradingActivityReport = ({
  rows,
  subFunds,
  assetClusters,
  assetAndGroupClusterMapToGroup,
  presetSettingsKey,
}: {
  rows: ITradingActivityReportQuery['bookkeeping']['tradingActivity'];
  subFunds: ITradingActivityReportQuery['portfolio']['subFunds']['list'];
  assetClusters: string[];
  assetAndGroupClusterMapToGroup: TupleKeyMap<[string, string], string>;
  presetSettingsKey: string;
}): ReactElement => {
  const gridApi = useRef<GridApi | null>(null);

  const { subAccountAndDimensionToSubFund, subFundDimensions } = mapSubAccountAndDimensionToSubFund(subFunds);
  const columns: (ColDef<RowData> | ColGroupDef<RowData>)[] = useMemo(
    () => [
      {
        headerName: 'Date',
        field: 'day',
        type: 'dateColumn',
        initialSort: 'desc',
        valueGetter: (params: ValueGetterParams<RowData>): string | undefined =>
          params.data ? dateReadableValueGetter(params.data.day) : undefined,
      },
      {
        headerName: 'Account Details',
        colId: 'account-details',
        marryChildren: true,
        children: [accountColumn(), subAccountColumn({ initialHide: true }), venueColumn({ initialHide: true })],
      },
      {
        headerName: 'Sub-funds',
        colId: 'sub-funds',
        marryChildren: true,
        children: subFundDimensions.map((subFundDimension) =>
          subFundGroupColumn<RowData>(subFundDimension, subAccountAndDimensionToSubFund)
        ),
      },
      {
        headerName: 'Asset Details',
        colId: 'asset-details',
        marryChildren: true,
        children: [
          assetWithSymbolColumn({ initialHide: false }),
          underlyingAssetColumn({ initialHide: true }),
          ...assetClusters.map((cluster) => clusterColumn<RowData>(cluster, assetAndGroupClusterMapToGroup)),
        ],
      },
      {
        headerName: 'Amount',
        colId: 'ta-amount',
        type: ['numericColumn', 'extendedNumericColumn'],
        valueGetter: (params): number | undefined => {
          const amount = params.data?.amount;
          if (!amount) {
            return undefined;
          }

          return bignumber(amount).toNumber();
        },
      },
      {
        headerName: 'Price',
        colId: 'ta-price',
        type: ['numericColumn', 'cashColumn'],
        valueGetter: (params): number | undefined => {
          const price = params.data?.price;
          if (!price) {
            return undefined;
          }

          return bignumber(price).toNumber();
        },
      },
      {
        headerName: 'Value',
        colId: 'ta-value',
        type: ['numericColumn', 'cashColumn'],
        valueGetter: (params): number | undefined => {
          const value = params.data?.value;
          if (!value) {
            return undefined;
          }

          return bignumber(value).toNumber();
        },
      },
      {
        headerName: 'Notional',
        colId: 'ta-notional',
        type: ['numericColumn', 'cashColumn'],
        valueGetter: (params): number | undefined => {
          const value = params.data?.value;
          if (!value) {
            return undefined;
          }

          return bignumber(value).abs().toNumber();
        },
      },
      {
        colId: 'userType',
        headerName: 'Transaction type',
        type: 'textColumn',
        valueGetter: (params): string | undefined => {
          return formatEnum(params.data?.userType);
        },
      },
      {
        colId: 'legCount',
        headerName: 'Activity count',
        type: ['numericColumn', 'extendedNumericColumn'],
        valueGetter: (params): number | undefined => {
          const amount = params.data?.legCount;
          if (!amount) {
            return undefined;
          }

          return bignumber(amount).toNumber();
        },
      },
      {
        colId: 'state',
        headerName: 'State',
        type: 'textColumn',
        valueGetter: (params): string | undefined => {
          return formatEnum(params.data?.state);
        },
      },
      {
        colId: 'legType',
        headerName: 'Leg Type',
        type: 'textColumn',
        valueGetter: (params): string | undefined => {
          return formatEnum(params.data?.legType);
        },
      },
      {
        colId: 'side',
        headerName: 'Position Side',
        type: 'textColumn',
        valueGetter: (params): string | undefined => {
          return formatEnum(params.data?.side);
        },
      },
      {
        colId: 'attributedToAsset',
        headerName: 'Attributed to asset',
        type: 'textColumn',
        minWidth: 160,
        field: 'attributedToAssetId.symbol',
      },
    ],
    [assetClusters, assetAndGroupClusterMapToGroup, subAccountAndDimensionToSubFund, subFundDimensions]
  );

  return (
    <GAgGridPresets
      presetSettingsKey={presetSettingsKey}
      defaultPresets={defaultPresets}
      rowData={rows}
      sideBar={{
        toolPanels: ['columns', 'filters'],
      }}
      enableCharts
      cellSelection
      defaultColDef={{
        resizable: true,
        sortable: true,
        filter: true,
      }}
      onGridReady={(params: { api: GridApi }) => {
        gridApi.current = params.api;
      }}
      onGridPreDestroyed={() => {
        gridApi.current = null;
      }}
      autoSizeStrategy={{ type: 'fitCellContents' }}
      columnDefs={columns}
    />
  );
};
